import React, { useMemo } from 'react';
import {
    Autocomplete, Box, Checkbox, Chip, FormControl, InputLabel, MenuItem, Select, Stack,
    TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Whatshot } from '@mui/icons-material';
import { format, isValid, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import {
    setChecklistActe,
    setChecklistRange,
    setChecklistEtatsFilter,
    setChecklistElementsFilter,
    setChecklistHeatmap,
} from '../../../modules/conteneurMap/conteneurMap.slice';
import {
    CHECKLIST_ETAT,
    CHECKLIST_ETAT_OPTIONS,
    CHECKLIST_COLORS,
    countChecklistEtats,
} from '../../../modules/conteneurMap/conteneurMap.helpers';

const toIso = (d) => (d && isValid(d) ? format(d, 'yyyy-MM-dd') : undefined);
const fromIso = (s) => (s ? parseISO(s) : null);

/**
 * Deuxième ligne de la toolbar, visible en mode « Maintenance » (PAT-04) :
 * acte checklist (R11) · plage de dates (R6) · filtre d'état avec compteurs (R9) ·
 * filtre par élément en défaut (R9) · heatmap (R8).
 *
 * `rows` = conteneurs déjà restreints aux patrimoines cochés (R7) : les compteurs suivent la sélection.
 */
function ChecklistToolbar({ rows, actes, acteCourant }) {
    const dispatch = useDispatch();
    const checklist = useSelector((s) => s.conteneurMap.checklist);

    const counts = useMemo(() => countChecklistEtats(rows), [rows]);
    const elementOptions = acteCourant?.elements ?? [];

    const colorOf = (code, n) => {
        if (code === CHECKLIST_ETAT.RAS) return CHECKLIST_COLORS.RAS;
        if (code === CHECKLIST_ETAT.PROBLEME) return CHECKLIST_COLORS.PROBLEME_2;
        if (code === CHECKLIST_ETAT.TENTATIVE_IMPOSSIBLE) return CHECKLIST_COLORS.TENTATIVE_IMPOSSIBLE;
        return CHECKLIST_COLORS.NON_INSPECTE;
    };

    return (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
        >
            <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="checklist-acte-label">Acte</InputLabel>
                <Select
                    labelId="checklist-acte-label"
                    label="Acte"
                    value={checklist.acte ?? ''}
                    onChange={(e) => dispatch(setChecklistActe(e.target.value || null))}
                >
                    {actes.map((a) => (
                        <MenuItem key={a.code} value={a.code}>{a.libelle}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <DatePicker
                label="Du"
                value={fromIso(checklist.dateDebut)}
                maxDate={fromIso(checklist.dateFin) ?? undefined}
                onChange={(d) => { const v = toIso(d); if (v) dispatch(setChecklistRange({ dateDebut: v })); }}
                slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
            <DatePicker
                label="Au"
                value={fromIso(checklist.dateFin)}
                minDate={fromIso(checklist.dateDebut) ?? undefined}
                onChange={(d) => { const v = toIso(d); if (v) dispatch(setChecklistRange({ dateFin: v })); }}
                slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />

            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">État&nbsp;:</Typography>
                <ToggleButtonGroup
                    size="small"
                    value={checklist.etats ?? []}
                    onChange={(_e, val) => dispatch(setChecklistEtatsFilter(val))}
                >
                    {CHECKLIST_ETAT_OPTIONS.map((opt) => (
                        <ToggleButton key={opt.code} value={opt.code} sx={{ gap: 0.75, textTransform: 'none' }}>
                            <Box component="span" sx={{
                                width: 10, height: 10, borderRadius: '2px', bgcolor: colorOf(opt.code), display: 'inline-block',
                            }} />
                            {opt.label}
                            <Chip size="small" label={counts[opt.code] ?? 0} sx={{ height: 18, fontSize: 11 }} />
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Stack>

            {elementOptions.length > 0 && (
                <Autocomplete
                    multiple
                    size="small"
                    disableCloseOnSelect
                    options={elementOptions}
                    getOptionLabel={(o) => o.libelle}
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    value={elementOptions.filter((o) => (checklist.elements ?? []).includes(o.code))}
                    onChange={(_e, val) => dispatch(setChecklistElementsFilter(val.map((o) => o.code)))}
                    renderOption={(props, option, { selected }) => (
                        <li {...props} key={option.code}>
                            <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0 }} />
                            {option.libelle}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Élément en défaut" />}
                    sx={{ minWidth: 240 }}
                />
            )}

            <Tooltip title="Heatmap des défauts (poids = nombre d'éléments en défaut)">
                <ToggleButton
                    size="small"
                    value="heatmap"
                    selected={!!checklist.heatmap}
                    onChange={() => dispatch(setChecklistHeatmap(!checklist.heatmap))}
                    aria-label="Heatmap des défauts"
                >
                    <Whatshot fontSize="small" />
                </ToggleButton>
            </Tooltip>
        </Stack>
    );
}

export default ChecklistToolbar;
